import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { focusWithinRing } from './focus-ring';

/**
 * Selectable card around a native radio (design-spec §3 "Radio card"):
 * surface, radius 16, padding 20×22, 1px line; selected = 2px accent border.
 * The native radio (accent-color) keeps keyboard arrows, form semantics and
 * screen-reader grouping for free — group cards with the same `name` inside a
 * `<fieldset>` + `<legend>`.
 *
 * The padding shrinks by 1px when selected so the 2px border never shifts layout.
 * Works controlled (`checked` + `onChange`) or uncontrolled (`defaultChecked`).
 */
export interface RadioCardProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'title' | 'className'
  > {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned value, e.g. a price (16.5/700). */
  aside?: ReactNode;
  /** Tighter vertical padding (payment cards: 18). */
  compact?: boolean;
  className?: string;
}

export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(
  function RadioCard(
    {
      title,
      description,
      aside,
      compact,
      className,
      checked,
      disabled,
      id: idProp,
      ...inputProps
    },
    ref
  ) {
    const autoId = useId();
    const id = idProp ?? `radio-${autoId}`;
    const titleId = `${id}-title`;
    const descId = description ? `${id}-desc` : undefined;
    // Styling keys off `:checked` via `has-[]`, so controlled and uncontrolled
    // groups both render the selected border.
    const pad = compact
      ? 'px-[22px] py-[18px] has-[:checked]:px-[21px] has-[:checked]:py-[17px]'
      : 'px-[22px] py-5 has-[:checked]:px-[21px] has-[:checked]:py-[19px]';

    return (
      <label
        htmlFor={id}
        className={cn(
          'group flex cursor-pointer items-start gap-3.5 rounded-16 bg-sl-surface font-sans transition-colors',
          'border border-sl-line hover:border-sl-line-strong',
          'has-[:checked]:border-2 has-[:checked]:border-sl-accent',
          focusWithinRing,
          disabled && 'cursor-not-allowed opacity-60',
          pad,
          className
        )}
      >
        <input
          ref={ref}
          id={id}
          type="radio"
          checked={checked}
          disabled={disabled}
          aria-labelledby={titleId}
          aria-describedby={descId}
          className="mt-[3px] h-[18px] w-[18px] shrink-0 cursor-[inherit] accent-sl-accent"
          {...inputProps}
        />
        <span className="min-w-0 flex-1">
          <span
            id={titleId}
            className="block text-[16.5px] font-semibold text-sl-ink group-has-[:checked]:font-bold"
          >
            {title}
          </span>
          {description && (
            <span
              id={descId}
              className="mt-1 block text-[13.5px] leading-snug text-sl-muted"
            >
              {description}
            </span>
          )}
        </span>
        {aside && (
          <span className="shrink-0 text-[16.5px] font-bold text-sl-ink">
            {aside}
          </span>
        )}
      </label>
    );
  }
);
